import React from 'react'
import styled from 'styled-components'
import { useTheme } from '../../middleware/ThemeContext'
import NoPreviewIcon from '../../assets/icons/NoPreviewIcon';
import StyledText from '../../components/StyledComponents/StyledText';
import PlaceholderImage from "../../assets/images/placeholder-image.jpg";
import { formatDisplayValue } from './FieldWithRadio';
import { Data } from '../../types/types';

interface fieldData {
    label: string;
    key: string;
}
interface MergePreview {
    scope?: 'CONTACT' | 'ORGANIZATION';
    data?: Data | undefined;
    columns?: fieldData[];
    dynamicFields: {
        submissionFields: { id: number; name: string }[];
        crmFieldValues: { id: number; name: string }[];
    };
}

interface FieldProps {
    title: string;
    value: string;
}

const hasKey = (obj: Record<string, any>, key: string): boolean => {
  return key in obj;
};

const Field: React.FC<FieldProps> = ({ title, value }) => {
    const {theme} = useTheme();
    return (
    <div style={{ borderBottom: `1px solid ${theme.CorporateTheme.colors.border}`, padding: '10px 0px ' }}>
        <StyledText fontSize='12px'>{title}</StyledText>
        <StyledText fontSize='14px' textColor={theme.CorporateTheme.colors.textSecondary}
            style={{
                display: '-webkit-box',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                WebkitLineClamp: 4,
                WebkitBoxOrient: 'vertical'
            }}
        >
            {value}
        </StyledText>
    </div>
    )
}

const isEmpty = (val: any): boolean => {
  if (val == null) return true;
  if (Array.isArray(val) && val.length === 0) return true;
  if (typeof val === 'object' && Object.keys(val).length === 0) return true;
  if (val === '') return true;
  return false;
};

const MergePreview: React.FC<MergePreview> = ({ scope, columns, data, dynamicFields }) => {
    const {theme} = useTheme();

    const orderedFieldDefinitions = [
    ...(dynamicFields?.submissionFields || []),
    ...(dynamicFields?.crmFieldValues || [])
  ];
  return (
    <BackgroundCon theme={theme} style={{ height: 'calc(100vh - 290px)', overflow: 'auto' }}>
        <PreviewCon>
            {(!data?.metaData.id) ? 
            <NoPreview>
                <div className='text-center py-4'>
                    <NoPreviewIcon />
                    <StyledText htmlTag={'h4'} fontWeight={700}>No Preview Found Yet</StyledText>
                    <StyledText htmlTag={'p'} fontSize='12px' fontWeight={400} style={{ lineHeight: '20px' }} textColor={theme.CorporateTheme.colors.textSecondary}>Choose fields to include in the merged contact</StyledText>
                </div>
            </NoPreview>
            
            : 
            <div>
                    <ProfileCon theme={theme} >
                        <ProfileImage>
                        <img
                            src={data.metaData.image || PlaceholderImage}
                            alt={data.metaData.image}
                            onError={(e) => {
                            (e.target as HTMLImageElement).src = PlaceholderImage;
                            }}
                        />
                        </ProfileImage>
                        <NameContainer theme={theme}>
                            <span style={{ fontWeight: 600 }}>{scope == 'CONTACT' ? data?.metaData?.firstName?.value +' '+ data?.metaData?.lastName?.value : scope == 'ORGANIZATION' ? data?.metaData?.name?.value : null}</span>
                            <IdText theme={theme}>Id: {data.metaData.id}</IdText>
                        </NameContainer>
                    </ProfileCon>
                    <div>
                    {
                    columns?.map((column: fieldData) => {
                        const metaData = data.metaData ?? {};

                        if (!hasKey(metaData, column.key)) {
                        return null;
                        }

                        let value: any;

                        if (column.key === 'contactOrganizations') {
                        value = (metaData as Record<string, any>)[column.key]?.orgNames;
                        } 
                        // else if (column.key === 'contactRoles') {
                        //   value = (metaData as Record<string, any>)[column.key]?.roleNames;
                        // } 
                        else {
                        value = (metaData as Record<string, any>)[column.key]?.value;
                        }

                        const isEmpty = (val: any): boolean => {
                        if (val == null) return true; 
                        if (Array.isArray(val) && val.length === 0) return true;
                        if (typeof val === 'object' && Object.keys(val).length === 0) return true;
                        if (val === '') return true;
                        return false;
                        };

                        if (isEmpty(value)) {
                        return null;
                        }

                        return (
                        <Field
                            key={column.key}
                            title={column.label}
                            value={value}
                        />
                        );
                    })
                    }
                    {/* {data.formSubmissions.map((field) => (
                        <Field
                            key={field.id}
                            title={field.name}
                            value={formatDisplayValue(field.value, field.name)}
                        />
                    ))} */}
                    {orderedFieldDefinitions.map((fieldDef) => {
                        // Find matching field data from formSubmissions
                        const fieldData = data.formSubmissions?.find(
                        (f) => f.id === fieldDef.id || f.name === fieldDef.name
                        );
                        
                        // Skip if no data or value is empty
                        if (!fieldData || isEmpty(fieldData.value)) {
                        return null;
                        }
                        
                        return (
                        <Field
                            key={fieldDef.id}
                            title={fieldDef.name}
                            value={formatDisplayValue(fieldData.value, fieldDef.name)}
                        />
                        );
                    })}
                    {data?.privateNotes && data?.privateNotes?.length > 0 && (
                        
                        <Field 
                            title='Private Notes'
                            value={`Total Merged Notes: ${
                                data?.privateNotes.reduce((total, note) => total + note.pNotesCount, 0)
                            }`}
                        />
                        )
                    }
                    {data.contacts && data.contacts.length > 0 && (
                        <Field 
                            title='Contacts'
                            value={`Total Merged Contacts: ${
                                data.contacts.reduce((total, contact) => total + contact.contactsCount, 0)
                            }`}
                        />
                    )}
                    </div>
            </div>
            }
        </PreviewCon>
    </BackgroundCon>
  )
}

const BackgroundCon = styled.div`
    border-radius: 5px;
    padding: 12px 20px;
    margin-top: 20px;
    border: 1px solid ${({theme}) => theme.CorporateTheme.colors.count};
    background: ${({theme}) => theme.colors.contentBackground};
    position: relative;
    z-index: 1;

    &:before {
        content: '';
        background: ${({theme}) => theme.CorporateTheme.colors.containerShadow};
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;
        z-index: 0;
    }
`;

const PreviewCon = styled.div`
    position: relative;
    z-index: 2;
    height: 100%;
`;

const NoPreview = styled.div`
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
`;

const ProfileCon = styled.div`
    display: flex;
    justify-content: center;
    margin-bottom: 10px;
    padding-bottom: 10px;
    position: relative;
    &:after {
        content: '';
        width: 80%;
        height: 1px;
        background-color: ${({theme}) => theme.CorporateTheme.colors.border};
        position: absolute;
        bottom: 0;
    }
`;

const ProfileImage = styled.div`
    margin-right: 10px;
    & img {
        width: 34px;
        height: 34px;
        border-radius: 50%;
    }
`;

const NameContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  fontSize: '16px',
  color: theme.CorporateTheme.colors.textPrimary,
  fontFamily: '"Raleway", sans-serif',
  lineHeight: '20px',
//   gap: CorporateTheme.spacing.xs,
}));

const IdText = styled('span')(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 400,
  fontFamily: '"Lato", sans-serif',
  color: theme.CorporateTheme.colors.textSecondary,
  textAlign: 'left',
}));

export default MergePreview